import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { money } from "./OwnerCommercialUi";

const EMPTY = {
  name: "",
  category: "Freelancer",
  billingMode: "one_time",
  currency: "ARS",
  basePrice: "",
  estimatedCost: "",
  description: "",
};

export default function OwnerServicesSection({ services, onRefresh }) {
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const setField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await ownerApi.createService({
        ...form,
        basePrice: Number(form.basePrice || 0),
        estimatedCost: Number(form.estimatedCost || 0),
      });
      setForm(EMPTY);
      await onRefresh();
    } catch (err) {
      setError(formatApiError(err, "No se pudo guardar el servicio"));
    } finally {
      setSaving(false);
    }
  }

  async function toggle(service, active) {
    try {
      await ownerApi.updateService(service.id, { active });
      await onRefresh();
    } catch (err) {
      setError(formatApiError(err, "No se pudo actualizar el servicio"));
    }
  }

  return (
    <Stack spacing={2}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent component="form" onSubmit={submit}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <DesignServicesRoundedIcon />
            <Typography variant="h6" sx={{ fontWeight: 950 }}>Nuevo servicio</Typography>
          </Stack>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" }, gap: 1.4 }}>
            <TextField required label="Servicio" value={form.name} onChange={setField("name")} />
            <TextField label="Categoría" value={form.category} onChange={setField("category")} />
            <TextField select label="Forma de cobro" value={form.billingMode} onChange={setField("billingMode")}>
              <MenuItem value="one_time">Por trabajo</MenuItem>
              <MenuItem value="hourly">Por hora</MenuItem>
              <MenuItem value="monthly">Mensual</MenuItem>
              <MenuItem value="custom">Personalizado</MenuItem>
            </TextField>
            <TextField select label="Moneda" value={form.currency} onChange={setField("currency")}>
              <MenuItem value="ARS">ARS</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
            <TextField label="Precio base" type="number" inputProps={{ min: 0, step: "any" }} value={form.basePrice} onChange={setField("basePrice")} />
            <TextField label="Costo estimado" type="number" inputProps={{ min: 0, step: "any" }} value={form.estimatedCost} onChange={setField("estimatedCost")} />
            <TextField label="Descripción" value={form.description} onChange={setField("description")} sx={{ gridColumn: { md: "1 / -1" } }} />
          </Box>
          <Button type="submit" variant="contained" disabled={saving} sx={{ mt: 2, textTransform: "none", fontWeight: 900 }}>
            {saving ? "Guardando..." : "Guardar servicio"}
          </Button>
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ fontWeight: 950 }}>Catálogo personal</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" }, gap: 1.3 }}>
        {services.map((service) => (
          <Card key={service.id} variant="outlined" sx={{ borderRadius: 3, opacity: service.active ? 1 : .62 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>{service.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{service.category} · {money(service.basePrice, service.currency)}</Typography>
                  {service.description ? <Typography variant="body2" sx={{ mt: .7 }}>{service.description}</Typography> : null}
                </Box>
                <FormControlLabel
                  control={<Switch checked={Boolean(service.active)} onChange={(event) => toggle(service, event.target.checked)} />}
                  label={service.active ? "Activo" : "Inactivo"}
                />
              </Stack>
            </CardContent>
          </Card>
        ))}
        {!services.length ? <Typography color="text.secondary">Todavía no definiste servicios.</Typography> : null}
      </Box>
    </Stack>
  );
}
